import React from 'react';
import { useRouteError } from 'react-router-dom';
import { Home, RefreshCw, ArrowLeft } from 'lucide-react';
import { Button, ButtonLink } from '@shared/components';

export const ErrorPage: React.FC = () => {
    const error = useRouteError() as { status?: number; statusText?: string; message?: string };

    const getErrorMessage = () => {
        if (error?.status === 404) {
            return {
                title: 'Page Not Found',
                message: "Sorry, we couldn't find the page you're looking for.",
                icon: '🔍',
            };
        }

        if (error?.status && error.status >= 500) {
            return {
                title: 'Server Error',
                message: 'Something went wrong on our end. Please try again later.',
                icon: '🚨',
            };
        }

        return {
            title: 'Something went wrong',
            message: error?.message || 'An unexpected error occurred.',
            icon: '⚠️',
        };
    };

    const { title, message, icon } = getErrorMessage();

    return (
        <div className="min-h-screen flex items-center justify-center bg-mp-background px-4">
            <div className="max-w-lg w-full text-center">
                <div className="mb-8">
                    <div className="text-6xl mb-4">{icon}</div>
                    <h1 className="text-4xl font-bold text-mp-foreground mb-2">
                        {error?.status || 'Error'}
                    </h1>
                    <h2 className="text-xl font-semibold text-mp-foreground mb-4">
                        {title}
                    </h2>
                    <p className="text-mp-muted mb-8">{message}</p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <ButtonLink to="/" size="sm" startIcon={<Home className="w-4 h-4" />}>
                        Go Home
                    </ButtonLink>
                    <Button
                        type="button"
                        variant="neutral"
                        appearance="outline"
                        size="sm"
                        startIcon={<ArrowLeft className="w-4 h-4" />}
                        onClick={() => window.history.back()}
                    >
                        Go Back
                    </Button>
                    <Button
                        type="button"
                        variant="neutral"
                        appearance="outline"
                        size="sm"
                        startIcon={<RefreshCw className="w-4 h-4" />}
                        onClick={() => window.location.reload()}
                    >
                        Try Again
                    </Button>
                </div>

                {/* Show error details in development */}
                {process.env.NODE_ENV === 'development' && error && (
                    <details className="mt-8 text-left bg-mp-foreground/5 p-4 rounded-lg">
                        <summary className="cursor-pointer font-medium text-mp-foreground">
                            Error Details (Development)
                        </summary>
                        <pre className="mt-2 text-xs text-mp-muted overflow-auto">
                            {JSON.stringify(error, null, 2)}
                        </pre>
                    </details>
                )}
            </div>
        </div>
    );
};
